import { Component, Inject, OnDestroy, OnInit, ViewChild, ElementRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
    ApplicationFacade,
    Itinerary,
    JourneyDirection,
    JourneyPlannerItineraryTransportMode,
    Leg,
    OpenTripPlannerFacade,
    OpenTripPlannerService,
    OtpUtil,
    PlannerErrorMessage,
    SearchService,
    SessionService,
    TransportMode
} from '@ridango/components';
import {
    BaseComponent,
    DisruptionMessage,
    DisruptionMessageEntity,
    DisruptionMessageEntityType,
    DisruptionMessageResponse,
    DisruptionMessagesService,
    DisruptionMessageType,
    matchesStopId,
    TimeUtil
} from '@ridango/core';
import { BehaviorSubject, combineLatest, concat, interval, Subscription, timer } from 'rxjs';
import { FavoriteStop, JourneyPlannerFacade, JourneyPlannerService } from '@ridango/components/lib/journey-planner';
import { LatLng } from 'leaflet';
import { take, takeUntil } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import Env from '@ridango/core/env';
import { Temporal } from '@js-temporal/polyfill';
import { AppStartupService, PullToRefreshService } from '@ridango/core/lib/services';
import { PullToRefreshPages } from '@ridango/core/lib/services/pull-to-refresh.service';
import { FullRouteSearchParams } from '@ridango/components/lib/search/search.service';
import { Platform } from '@ionic/angular';
import groupBy from 'lodash/groupBy';
import { MapApiService } from '@ridango/widgets';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { Capacitor } from '@capacitor/core';

@Component({
    selector: 'gl-journey-planner-results',
    templateUrl: './journey-planner-results.component.html',
    styleUrls: ['./journey-planner-results.component.scss']
})
export class JourneyPlannerResultsComponent extends BaseComponent implements OnInit, OnDestroy {
    @ViewChild('favoriteButton', { read: ElementRef }) favoriteButton: ElementRef;
    @ViewChild('dummyFocus') dummyFocus!: ElementRef;
    assetsPath$ = this.applicationFacade.assetsPath$;
    itineraries$ = this.openTripPlannerFacade.itineraries$;
    search$ = this.openTripPlannerFacade.search$;
    plan$ = this.openTripPlannerFacade.plan$;
    searchRecentlyJourneys$ = this.openTripPlannerFacade.searchRecentlyJourneys$;
    disruptionMessages$ = new BehaviorSubject<DisruptionMessageResponse>(null);
    physicalBackButtonSubscription$;
    currentTime$: BehaviorSubject<number>;
    otpUtil = OtpUtil;
    PlannerErrorMessageEnum = PlannerErrorMessage;
    displayedItineraries: Itinerary[] = [];
    displayedGroupItineraries: { date: string; itineraries: Itinerary[] }[];
    selectedTime: string;
    currentPlan;
    isLocationExistInFavoriteList: boolean;
    search = null;
    favoriteChanged = null;
    searchRecentlyJourneys = [];
    isGroupedRoutes = this.env.JOURNEY_PLANNER.SHOW_GROUPED_STOP_ROUTES;
    minimumVisibleDistance: number = this.env?.JOURNEY_PLANNER?.MINIMUM_VISIBLE_DISTANCE ?? 0;
    journeyPlannerFavoriteId: string | number;
    foundedFavoriteStopId: string | number;
    showEarlier = false;
    private initialLoad = true;
    private arriveBy: boolean;
    private loadMore: 'BEFORE' | 'AFTER' | null = null;
    private storedItineraries: Itinerary[] = [];
    private timeZone = this.appStartupService.getTimezone();
    private SHOW_BEFORE = 0;
    private SHOW_AFTER = 4;
    private AVAILABLE_BEFORE = 0;
    private AVAILABLE_AFTER = 0;
    private LOAD_MORE_BEFORE_STEP = 0;
    private LOAD_MORE_AFTER_STEP = 0;
    protected DEFAULT_LOAD_MORE = 4;
    private allAfterItineraries: Itinerary[] = [];
    private allBeforeItineraries: Itinerary[] = [];
    private allBeforeCurrentTime: Itinerary[] = [];

    private selectedItinerary: {
        tripId: string;
    };
    private paramsKeysEnum = {
        ORIGIN: 'origin',
        DESTINATION: 'destination',
        ITINERARY: 'itinerary',
        SEARCHDATEANDTIME: 'searchDateAndTime'
    };
    isIos = Capacitor.getPlatform() === 'ios';
    private _currentTimeSubscription: Subscription;
    public isProcessingSharedItinerary = false;
    private favoriteStops: FavoriteStop[] = [];

    constructor(
        @Inject('ENV') public env: Env,
        private applicationFacade: ApplicationFacade,
        private openTripPlannerFacade: OpenTripPlannerFacade,
        private openTripPlannerService: OpenTripPlannerService,
        private journeyPlannerFacade: JourneyPlannerFacade,
        private journeyPlannerService: JourneyPlannerService,
        private appStartupService: AppStartupService,
        private mapApi: MapApiService,
        private router: Router,
        private route: ActivatedRoute,
        private readonly searchService: SearchService,
        private translate: TranslateService,
        private disruptionMessagesService: DisruptionMessagesService,
        private pullToRefresh: PullToRefreshService,
        private sessionService: SessionService,
        private platform: Platform,
        private liveAnnouncer: LiveAnnouncer,
        private appConfig: AppStartupService
    ) {
        super();
        this.initializeLocationFromNavigation();
    }

    ngOnInit(): void {
        this.setCurrentTimeIntervalUpdate();
        this.resetShowCounts();
        this.physicalBackButtonSubscription$ = this.platform.backButton.subscribeWithPriority(1000, async () => {
            await this.redirectToJourneyPlanner();
        });

        this.pullToRefresh.enablePullToRefresh(PullToRefreshPages.JourneyPlannerResults);

        this.pullToRefresh.methodCalled$.pipe(takeUntil(this.componentDestroyed$)).subscribe(() => {
            this.initialLoad = true;
            this.loadMore = null;
            this.setCurrentTimeIntervalUpdate();
            this.resetShowCounts();
            this.refreshData();
        });

        interval(1000)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(() => {
                this.setLoadMoreToPast(this.getCurrentTimeInMs());
            });

        if (this.env.JOURNEY_PLANNER?.SHOW_SEARCH_HISTORY) {
            this.searchRecentlyJourneys$
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe((searchRecentlyJourneys) => {
                    this.searchRecentlyJourneys = searchRecentlyJourneys;
                });
        }

        this.handleImportedPlan();

        this.journeyPlannerFacade.favoriteStops$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((favoriteStops) => {
                this.favoriteStops = favoriteStops;
                this.processFavoriteStops(favoriteStops);
            });

        this.search$.pipe(takeUntil(this.componentDestroyed$)).subscribe((search) => {
            this.initialLoad = true;
            this.arriveBy = search?.searchDateAndTime?.arrive;
            this.allBeforeItineraries = [];
            this.allAfterItineraries = [];
            this.allBeforeCurrentTime = [];
            this.resetShowCounts();
            this.search = search;
            if (search?.destination && search?.origin) {
                this.processFavoriteStops(this.favoriteStops);
                this.processSearch(search as FullRouteSearchParams);
            } else {
                this.currentPlan = null;
                this.journeyPlannerFavoriteId = undefined;
            }
        });

        if (this.env.JOURNEY_PLANNER?.SHOW_DISRUPTION_MESSAGES) {
            combineLatest([this.itineraries$, this.disruptionMessages$])
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe(([itineraries, disruptionMessages]) => {
                    this.updateItineraries(itineraries);
                    this.formatItineraries(this.storedItineraries, disruptionMessages?.messages);
                });
        } else {
            this.itineraries$.pipe(takeUntil(this.componentDestroyed$)).subscribe((itineraries) => {
                this.updateItineraries(itineraries);
                this.formatItineraries(this.storedItineraries);
            });
        }
        if (this.env.JOURNEY_PLANNER?.SHOW_FAVORITES) {
            this.journeyPlannerFacade.getFavoriteStops();
        }
        this.sessionService.removeJourneyQueryParams();
        this.mapApi.removeStopsFromMap();
    }

    getSecondsToNextUpdate(currentSeconds: number, updateIntervalSeconds: number): number {
        const remainder = currentSeconds % updateIntervalSeconds;
        return remainder === 0 ? updateIntervalSeconds : updateIntervalSeconds - remainder;
    }

    private updateItineraries(newItineraries: Itinerary[]) {
        const existingItinerariesMap = new Map(this.storedItineraries.map((it) => [this.getUniqueKey(it), it]));
        newItineraries.forEach((itinerary) => {
            const key = this.getUniqueKey(itinerary);
            existingItinerariesMap.set(key, itinerary);
        });
        this.storedItineraries = Array.from(existingItinerariesMap.values());
    }

    getUniqueKey(itinerary: Itinerary): string {
        if (itinerary?.journeyKey) {
            return itinerary.journeyKey;
        }
        const legWithTripId = itinerary.legs.find((leg) => leg.tripId);
        if (legWithTripId) {
            return legWithTripId.tripId;
        }
        return itinerary.startTime.toString();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.pullToRefresh.disablePullToRefresh();
        this.physicalBackButtonSubscription$?.unsubscribe();
    }

    private processFavoriteStops(favoriteStops: FavoriteStop[]): void {
        this.foundedFavoriteStopId = favoriteStops.find((stop) =>
            this.journeyPlannerService.checkFavoriteStops(stop, this.searchFavoritePlan)
        )?.id;

        this.isLocationExistInFavoriteList = favoriteStops.some((stop) =>
            this.journeyPlannerService.checkFavoriteStops(stop, this.searchFavoritePlan)
        );
    }

    private async processSearch(search: FullRouteSearchParams): Promise<void> {
        this.searchService.setPickedRouteSearchParams(search as FullRouteSearchParams);
        const payload = await this.getPayloadForPlanRequest(search);

        this.openTripPlannerFacade.getPlan(payload);

        this.disruptionMessagesService
            .getMessages()
            .pipe(take(1))
            .subscribe((res) => {
                this.disruptionMessages$.next(res);
            });

        this.currentPlan = {
            id: this.journeyPlannerFavoriteId || this.foundedFavoriteStopId,
            ...payload,
            fromPlaceName: search.origin.name,
            toPlaceName: search.destination.name,
            fromStopId: search.origin?.stopId ?? payload.fromPlace,
            toStopId: search.destination.stopId ?? payload.toPlace
        };
    }

    handleRefresh(event) {
        this.refreshData();
        event.target.complete();
    }

    getCurrentTimeInMs = () =>
        Temporal.Now.instant().toZonedDateTimeISO(this.appConfig.getTimezone()).epochMilliseconds;

    private getStartOfDayInMs(): number {
        const timezone = this.appConfig.getTimezone();
        return Temporal.Now.zonedDateTimeISO(timezone).with({ hour: 0, minute: 0, second: 0, millisecond: 0 })
            .epochMilliseconds;
    }
    private handleImportedPlan(): void {
        const queryParams = this.route.snapshot.queryParams;
        if (!Object.keys(queryParams)?.length) {
            return;
        }
        Object.entries(queryParams).forEach(([key, value]) => {
            const item = this.tryParseJSON(value);
            switch (key) {
                case this.paramsKeysEnum.ITINERARY:
                    this.isProcessingSharedItinerary = true;
                    this.selectedItinerary = item;
                    break;
                case this.paramsKeysEnum.SEARCHDATEANDTIME:
                    this.currentTime$.pipe(take(1)).subscribe((currentTime) => {
                        const dateTimeStr = `${item.date}T${item.time}:00`;
                        const dateTime = Temporal.PlainDateTime.from(dateTimeStr).toZonedDateTime(
                            this.appConfig.getTimezone()
                        ).epochMilliseconds;
                        if (currentTime < dateTime) {
                            this.openTripPlannerFacade.setSearchDateAndTime(item.date, item.time, item.arrive);
                        }
                    });
                    break;
                default:
                    this.searchService.tickSearchPoint(key, {
                        coordinates: item.coordinates,
                        municipalityName: item.municipalityName,
                        name: item.name,
                        useCurrentLocation: false
                    });
            }
        });
    }

    private tryParseJSON(jsonString) {
        try {
            const obj = JSON.parse(jsonString.includes('%') ? decodeURIComponent(jsonString) : jsonString);

            if (obj && typeof obj === 'object') {
                return obj;
            }
        } catch (e) {
            console.warn('Error occurred while parsing JSON string');
        }
        return jsonString;
    }

    private findObjectByTripId(data, tripId) {
        return data.find((item) => item.legs.some((leg) => leg.tripId === tripId));
    }

    showMoreClickAfter(): void {
        this.initialLoad = false;
        const loadMoreAmount = this.env.JOURNEY_PLANNER?.SHOW_LAST_DEPARTED_JOURNEYS?.JOURNEYS_AFTER_ARRIVE_BY_MORE;
        if (loadMoreAmount) {
            this.loadMore = 'AFTER';
            this.SHOW_AFTER += loadMoreAmount;
        } else {
            this.SHOW_AFTER += this.DEFAULT_LOAD_MORE;
        }

        if (this.AVAILABLE_AFTER < this.SHOW_AFTER + this.LOAD_MORE_AFTER_STEP) {
            this.getMore();
        }
        this.disruptionMessages$.pipe(take(1)).subscribe((disruptionResponse) => {
            this.formatItineraries(this.storedItineraries, disruptionResponse?.messages || []);
        });
    }

    showMoreClickBefore(): void {
        this.initialLoad = false;
        const loadMoreAmount = this.env.JOURNEY_PLANNER?.SHOW_LAST_DEPARTED_JOURNEYS?.JOURNEYS_BEFORE_ARRIVE_BY_MORE;
        if (loadMoreAmount) {
            this.loadMore = 'BEFORE';
            this.SHOW_BEFORE += loadMoreAmount;
        } else {
            this.SHOW_BEFORE += this.DEFAULT_LOAD_MORE;
        }
        if (this.AVAILABLE_BEFORE < this.SHOW_BEFORE + this.LOAD_MORE_BEFORE_STEP) {
            this.getMore();
        }
        this.disruptionMessages$.pipe(take(1)).subscribe((disruptionResponse) => {
            this.formatItineraries(this.storedItineraries, disruptionResponse?.messages || []);
        });
    }

    getTotalToShow() {
        return this.SHOW_BEFORE + this.SHOW_AFTER;
    }

    getTotalShown(): number {
        if (this.isGroupedRoutes) {
            return this.displayedGroupItineraries.reduce((sum, group) => sum + (group.itineraries?.length ?? 0), 0);
        } else {
            return this.displayedItineraries?.length ?? 0;
        }
    }

    showMoreAfter(): boolean {
        if (this.env?.JOURNEY_PLANNER?.SHOW_LAST_DEPARTED_JOURNEYS?.JOURNEYS_AFTER_ARRIVE_BY_MORE) {
            return true;
        }

        const totalShown = this.getTotalShown();
        const totalToShow = this.getTotalToShow();

        return totalShown > 0 && totalShown < totalToShow;
    }

    openItineraryDetailsView(itinerary: Itinerary) {
        this.router.navigate([`itinerary/details`], { relativeTo: this.route.parent }).then(() => {
            this.openTripPlannerFacade.setSelectedItineraryForDetails(itinerary);
            this.isProcessingSharedItinerary = false;
        });
    }

    setSelectedTime(date: string): void {
        this.selectedTime = date;
    }

    isDate(stringDate: string): boolean {
        return !isNaN(Date.parse(stringDate));
    }

    private groupJourneyResultByDayDate(itineraries: Itinerary[]): { date: string; itineraries: Itinerary[] }[] {
        const timeZone = this.appConfig.getTimezone();
        const today = Temporal.Now.plainDateTimeISO(timeZone);

        const groupedItineraries = groupBy(itineraries, (itinerary) => {
            const tripDate = Temporal.Instant.fromEpochMilliseconds(itinerary.startTime)
                .toZonedDateTimeISO(timeZone)
                .toPlainDate();
            const selectedDate = this.selectedTime ? Temporal.PlainDate.from(this.selectedTime) : null;
            switch (true) {
                case tripDate.equals(selectedDate ?? today):
                    return tripDate.toString();
                default:
                    return tripDate.toString();
            }
        });

        return Object.keys(groupedItineraries).map((date) => ({ date, itineraries: groupedItineraries[date] }));
    }

    getActualDepartureTime = (itinerary: Itinerary) => {
        const leg = itinerary.legs.find((l) => l.mode !== TransportMode.Walk);
        const delay = (itinerary?.journeyStartDepartureDelay ?? leg?.departureDelay) || 0;
        return itinerary.startTime + delay * 1000;
    };

    getActualArrivalTime = (itinerary: Itinerary) => {
        const leg = itinerary.legs.find((l) => l.mode !== TransportMode.Walk);
        const delay = (itinerary?.journeyEndArrivalDelay ?? leg?.arrivalDelay) || 0;
        return itinerary.endTime + delay * 1000;
    };

    private formatItineraries(itineraries: Itinerary[], disruptionMessages?: DisruptionMessage[]): void {
        const arriveBy = this.search?.searchDateAndTime?.arrive;
        this.arriveBy = arriveBy;
        if (this.selectedItinerary && itineraries) {
            const sharedItinerary = this.findObjectByTripId(itineraries, this.selectedItinerary.tripId);
            this.currentTime$.pipe(take(1), takeUntil(this.componentDestroyed$)).subscribe((currentTime) => {
                if (sharedItinerary && !(currentTime > sharedItinerary.startTime)) {
                    this.openItineraryDetailsView(this.formatItinerary(sharedItinerary, disruptionMessages));
                    return;
                } else {
                    const nextItinerary = itineraries.find((itinerary) => {
                        return !(currentTime > itinerary.startTime);
                    });
                    if (nextItinerary) {
                        this.openItineraryDetailsView(this.formatItinerary(nextItinerary, disruptionMessages));
                    } else {
                        return;
                    }
                }
                this.isProcessingSharedItinerary = false;
            });
        }
        const currentTime = this.getCurrentTimeInMs();

        const targetTime = this.search?.searchDateAndTime?.time
            ? Temporal.PlainDateTime.from(
                  `${this.search.searchDateAndTime.date}T${this.search.searchDateAndTime.time}`
              ).toZonedDateTime(this.appConfig.getTimezone()).epochMilliseconds
            : currentTime;

        const filterTimeBoundary = this.env.ENABLE_FULL_TIME_SELECTION ? this.getStartOfDayInMs() : currentTime;
        const [pastItineraries, upcomingItineraries] = itineraries
            .reduce(
                ([past, upcoming], itinerary) => {
                    const actualDepartureTime = this.getActualDepartureTime(itinerary);
                    if (arriveBy ? filterTimeBoundary > actualDepartureTime : targetTime >= actualDepartureTime) {
                        past.push(itinerary);
                    } else {
                        upcoming.push(itinerary);
                    }
                    return [past, upcoming];
                },
                [[], []] as [Itinerary[], Itinerary[]]
            )
            .map((group) => {
                return group.map((itinerary) => ({
                    ...itinerary,
                    legs: this.formatLegs(itinerary.legs, disruptionMessages)
                }));
            });

        const slicesItineraries = this.selectItinerariesToShow(
            [...pastItineraries, ...upcomingItineraries],
            targetTime,
            currentTime
        );

        if (slicesItineraries?.length) {
            const message = this.translate.instant('FOUND_N_RESULTS', { count: slicesItineraries.length });
            this.liveAnnouncer.announce(message);
        }

        if (arriveBy && this.env.JOURNEY_PLANNER?.SORT_BY_CLOSEST_ARRIVAL) {
            upcomingItineraries.sort((a, b) => {
                const endTimeA = new Date(a.endTime).getTime();
                const endTimeB = new Date(b.endTime).getTime();
                return endTimeB - endTimeA;
            });
        }

        if (this.isGroupedRoutes) {
            this.displayedGroupItineraries = this.groupJourneyResultByDayDate(slicesItineraries);
        } else {
            this.displayedItineraries = slicesItineraries;
        }
        this.setCurrentTimeIntervalUpdate();
        this.loadMore = null;
        this.setLoadMoreToPast(this.getCurrentTimeInMs());
    }

    // Select itineraries to show based on the current time and target time from slices
    // The returned array of itineraries is final product of filtering and sorted ready for UI
    selectItinerariesToShow(itineraries: Itinerary[], targetTime, currentTime): Itinerary[] {
        if (!itineraries.length) return [];
        const maxPastJourneys = this.env.JOURNEY_PLANNER?.SHOW_LAST_DEPARTED_JOURNEYS?.MAX_JOURNEYS ?? 0;

        // When user selects a time in the past, use that as reference instead of current time
        // This allows showing past journeys with the same logic as present/future journeys
        const referenceTime = Math.min(targetTime, currentTime);

        // Sort itineraries by actual departure time
        if (this.arriveBy) {
            itineraries.sort((a, b) => this.getActualArrivalTime(a) - this.getActualArrivalTime(b));
        } else {
            itineraries.sort((a, b) => this.getActualDepartureTime(a) - this.getActualDepartureTime(b));
        }

        this.allAfterItineraries = [];
        this.allBeforeItineraries = [];
        this.allBeforeCurrentTime = [];

        for (const itinerary of itineraries) {
            const itineraryTime = this.arriveBy
                ? this.getActualArrivalTime(itinerary)
                : this.getActualDepartureTime(itinerary);
            if (
                this.arriveBy
                    ? itineraryTime > targetTime
                    : itineraryTime >= targetTime && this.getActualDepartureTime(itinerary) > referenceTime
            ) {
                this.allAfterItineraries.push(itinerary);
            } else if (itineraryTime < referenceTime) {
                this.allBeforeCurrentTime.push(itinerary);
            } else if (
                this.arriveBy
                    ? itineraryTime <= targetTime
                    : itineraryTime < targetTime && this.getActualDepartureTime(itinerary) > referenceTime
            ) {
                this.allBeforeItineraries.push(itinerary);
            }
        }

        // Get total available before and after
        this.AVAILABLE_BEFORE = this.allBeforeItineraries.length;
        this.AVAILABLE_AFTER = this.allAfterItineraries.length;

        // Select SHOW_BEFORE and SHOW_AFTER amounts
        const pastItineraries = this.allBeforeCurrentTime.slice(-maxPastJourneys);
        let beforeItineraries = this.allBeforeItineraries.slice(-this.SHOW_BEFORE);
        let afterItineraries = this.allAfterItineraries.slice(0, this.SHOW_AFTER);

        // keep optimal journey visually pleasing location
        if (this.arriveBy) {
            const optimalItinerary = beforeItineraries.find((i) => i?.isMostOptimal);
            if (optimalItinerary) {
                beforeItineraries = beforeItineraries.filter((i) => i !== optimalItinerary);
                beforeItineraries.push(optimalItinerary);
            }
        } else if (!this.arriveBy) {
            const optimalItinerary = afterItineraries.find((i) => i?.isMostOptimal);
            if (optimalItinerary) {
                afterItineraries = afterItineraries.filter((i) => i !== optimalItinerary);
                afterItineraries.unshift(optimalItinerary);
            }
        }

        return [...pastItineraries, ...beforeItineraries, ...afterItineraries];
    }

    resetShowCounts() {
        this.storedItineraries = [];

        const settings = this.env?.JOURNEY_PLANNER?.SHOW_LAST_DEPARTED_JOURNEYS;
        const journeysBeforeArriveBy = settings?.JOURNEYS_BEFORE_ARRIVE_BY;
        const journeysAfterArriveBy = settings?.JOURNEYS_AFTER_ARRIVE_BY;
        const journeysAfterArriveByMore = settings?.JOURNEYS_AFTER_ARRIVE_BY_MORE;
        const journeysBeforeArriveByMore = settings?.JOURNEYS_BEFORE_ARRIVE_BY_MORE;
        const includeOptimal = settings?.INCLUDE_OPTIMAL_JOURNEY;

        if (this.arriveBy && this.initialLoad) {
            this.SHOW_BEFORE = (journeysBeforeArriveBy ?? this.DEFAULT_LOAD_MORE) + (includeOptimal ? 1 : 0);
            this.SHOW_AFTER = journeysAfterArriveBy ?? this.DEFAULT_LOAD_MORE;
        } else if (!this.arriveBy && this.initialLoad) {
            this.SHOW_BEFORE = 1;
            this.SHOW_AFTER = this.DEFAULT_LOAD_MORE;
        } else if (this.arriveBy && !this.initialLoad) {
            this.SHOW_BEFORE = journeysAfterArriveByMore ?? this.DEFAULT_LOAD_MORE;
            this.SHOW_AFTER = journeysBeforeArriveByMore ?? this.DEFAULT_LOAD_MORE;
        } else if (!this.arriveBy && !this.initialLoad) {
            this.SHOW_BEFORE = journeysAfterArriveByMore ?? this.DEFAULT_LOAD_MORE;
            this.SHOW_AFTER = (journeysBeforeArriveByMore ?? this.DEFAULT_LOAD_MORE) + (includeOptimal ? 1 : 0);
        } else {
            this.SHOW_AFTER = this.DEFAULT_LOAD_MORE;
        }
    }

    setLoadMoreToPast(currentTime: number) {
        const shouldShow =
            this.search?.searchDateAndTime?.arrive &&
            !!this.env?.JOURNEY_PLANNER?.SHOW_LAST_DEPARTED_JOURNEYS?.JOURNEYS_BEFORE_ARRIVE_BY_MORE;

        const isSomeDeparted = [...this.allBeforeItineraries, ...this.allBeforeCurrentTime].some(
            (itinerary) => this.getActualDepartureTime(itinerary) < currentTime
        );
        this.showEarlier = shouldShow && !isSomeDeparted;
    }

    private formatItinerary(itinerary: Itinerary, disruptionMessages?: DisruptionMessage[]): Itinerary {
        return {
            ...itinerary,
            legs: this.formatLegs(itinerary.legs, disruptionMessages)
        };
    }

    private formatLegs(legs: Leg[], disruptionMessages?: DisruptionMessage[]): Leg[] {
        const newLegsAry = [];

        legs.forEach((leg, index) => {
            const isWalkMinDistance = leg.mode === TransportMode.Walk && leg.distance < this.minimumVisibleDistance;
            const legDisruptionMessages = this.getAlertsForLeg(leg, disruptionMessages);

            if (index === 0) {
                if (isWalkMinDistance) {
                    return;
                }
                newLegsAry.push({
                    ...leg,
                    duration: Temporal.Duration.from({ seconds: leg.duration }),
                    disruptionMessages: legDisruptionMessages
                });
            } else {
                const previousLeg = legs[index - 1];
                if (previousLeg.endTime && leg.startTime !== previousLeg.endTime) {
                    const timeDiff = Temporal.Instant.fromEpochMilliseconds(
                        previousLeg.endTime + previousLeg.arrivalDelay * 1000
                    ).until(Temporal.Instant.fromEpochMilliseconds(leg.startTime + leg.departureDelay * 1000));
                    if (timeDiff.total({ unit: 'seconds' }) > 0) {
                        const waitLeg = {
                            duration: timeDiff,
                            mode: JourneyPlannerItineraryTransportMode.Wait
                        };
                        newLegsAry.push(waitLeg);
                    }
                }
                if (isWalkMinDistance) {
                    return;
                }
                newLegsAry.push({
                    ...leg,
                    duration: Temporal.Duration.from({ seconds: leg.duration }),
                    disruptionMessages: legDisruptionMessages
                });
            }
        });

        return newLegsAry;
    }

    private getAlertsForLeg(leg: Leg, disruptionMessages?: DisruptionMessage[]): DisruptionMessage[] {
        const legDisruptionMessages = [];
        if (this.env.JOURNEY_PLANNER?.SHOW_DISRUPTION_MESSAGES && leg.mode !== TransportMode.Walk) {
            disruptionMessages?.forEach((message) => {
                if (this.checkAlertBus(message, leg) || this.checkAlertStop(message, leg)) {
                    legDisruptionMessages.push(message);
                }
            });
        }
        return legDisruptionMessages;
    }

    private matchesRouteEntity(entity: DisruptionMessageEntity, leg: Leg): boolean {
        return entity.type === DisruptionMessageEntityType.ROUTE && entity.routeShortName === leg.routeShortName;
    }

    private matchesStopEntity(entity: DisruptionMessageEntity, leg: Leg): boolean {
        if (entity.type !== DisruptionMessageEntityType.STOP) {
            return false;
        }

        const allStops = [leg.from, leg.to, ...(leg.intermediateStops || [])];
        return allStops.some((stop) => matchesStopId(entity.stopId, stop?.stopId, stop?.stopCode));
    }

    private checkAlertBus(message: DisruptionMessage, leg: Leg): boolean {
        const isBusAlert =
            message.messageType === DisruptionMessageType.ALERT_BUS ||
            message.messageType === DisruptionMessageType.ALERT_BUS_AND_STOP;

        return (
            isBusAlert &&
            message.informedEntities.some((entity) => this.matchesRouteEntity(entity, leg)) &&
            this.isAlertWithinActivePeriod(leg, message)
        );
    }

    private checkAlertStop(message: DisruptionMessage, leg: Leg): boolean {
        const isStopAlert =
            message.messageType === DisruptionMessageType.ALERT_STOP ||
            message.messageType === DisruptionMessageType.ALERT_BUS_AND_STOP;

        return (
            isStopAlert &&
            message.informedEntities.some((entity) => this.matchesStopEntity(entity, leg)) &&
            this.isAlertWithinActivePeriod(leg, message)
        );
    }

    private initializeLocationFromNavigation(): void {
        const navigation = this.router.getCurrentNavigation();
        const location = navigation?.extras.state?.data;
        this.journeyPlannerFavoriteId = location?.id;

        if (location) {
            this.openTripPlannerFacade.setSearchAddress(
                JourneyDirection.Origin,
                location.fromPlaceName,
                new LatLng(location.fromPlace.lat, location.fromPlace.lng),
                false,
                null,
                null,
                location.fromPlace?.stopId,
                null
            );

            this.openTripPlannerFacade.setSearchAddress(
                JourneyDirection.Destination,
                location.toPlaceName,
                new LatLng(location.toPlace.lat, location.toPlace.lng),
                false,
                null,
                null,
                null,
                location.toPlace?.stopId
            );
        }
    }

    async redirectToJourneyPlanner(): Promise<void> {
        this.openTripPlannerFacade.clearSearch();
        this.mapApi.addStopsToMap();
        await this.router.navigate([this.translate.currentLang, 'journey-planner']);
    }

    private isAlertWithinActivePeriod(leg: Leg, message: DisruptionMessage): boolean {
        const legStartTime = Temporal.Instant.fromEpochMilliseconds(leg.startTime).epochMilliseconds;
        const messageActivePeriodStart = Temporal.Instant.from(message.activePeriodStart.toString()).epochMilliseconds;
        const messageActivePeriodEnd = Temporal.Instant.from(message.activePeriodEnd.toString()).epochMilliseconds;
        return legStartTime >= messageActivePeriodStart && legStartTime <= messageActivePeriodEnd;
    }

    private get searchFavoritePlan(): FavoriteStop {
        if (!this.search) {
            return null;
        }

        const { origin, destination } = this.search;

        return {
            id: this.journeyPlannerFavoriteId || this.foundedFavoriteStopId,
            fromPlace: {
                ...origin?.coordinates,
                name: origin?.name,
                stopId: this.currentPlan?.fromPointType === 'STOP_AREA' ? this.currentPlan?.fromStopId : null
            },
            toPlace: {
                ...destination?.coordinates,
                name: destination?.name,
                stopId: this.currentPlan?.toPointType === 'STOP_AREA' ? this.currentPlan?.toStopId : null
            },
            municipalityName: {
                origin: origin?.municipalityName,
                destination: destination?.municipalityName
            },
            fromPlaceName: origin?.name,
            toPlaceName: destination?.name
        };
    }

    isPlannerErrorMessage(error: PlannerErrorMessage): boolean {
        return Object.values(PlannerErrorMessage).includes(error);
    }

    showError() {
        const noItineraries = !(
            !this.isProcessingSharedItinerary &&
            this.search?.destination &&
            this.search?.origin &&
            [...this.allAfterItineraries, ...this.allBeforeItineraries, ...this.allBeforeCurrentTime]?.length === 0
        );
        return (
            (!noItineraries && !this.showHistory()) ||
            this.isSameLocations(this.search?.origin?.coordinates, this.search?.destination?.coordinates)
        );
    }

    showHistory() {
        return (
            (!this.search?.destination || !this.search?.origin) &&
            this.searchRecentlyJourneys?.length &&
            this.env.JOURNEY_PLANNER?.SHOW_SEARCH_HISTORY
        );
    }

    addFavoritePath(): void {
        if (this.isLocationExistInFavoriteList) {
            this.journeyPlannerFacade.deleteFavoriteStop(this.searchFavoritePlan);
        } else {
            this.liveAnnouncer.announce(this.translate.instant('FAVORITE_ADDED'));
            this.journeyPlannerFacade.addFavoriteStop(this.searchFavoritePlan);
        }

        this.journeyPlannerFavoriteId = undefined;
    }

    refreshData(): void {
        this.initialLoad = true;
        this.resetShowCounts();
        this.search$.pipe(take(1)).subscribe(async (search) => {
            if (search?.destination && search?.origin) {
                const payload = await this.getPayloadForPlanRequest(search);

                this.openTripPlannerFacade.getPlan(payload);
            }
        });
    }

    getMore(): void {
        this.search$.pipe(take(1)).subscribe(async (search) => {
            if (search?.destination && search?.origin) {
                const payload = await this.getPayloadForPlanRequest(search);
                this.openTripPlannerFacade.getPlan(payload);
            }
        });
    }

    async getPayloadForPlanRequest(search) {
        let payload;

        let searchFrom: { date: string; time: string; arrive: boolean } | null = null;

        if (this.loadMore === 'BEFORE') {
            const earliestStartTime =
                this.storedItineraries.length > 0
                    ? Math.min(...this.storedItineraries.map((itinerary) => itinerary.startTime))
                    : null;

            if (earliestStartTime) {
                const zonedDateTime = Temporal.Instant.fromEpochMilliseconds(earliestStartTime).toZonedDateTimeISO(
                    this.timeZone
                );

                searchFrom = {
                    date: zonedDateTime.toPlainDate().toString(),
                    time: zonedDateTime.toPlainTime().toString(),
                    arrive: this.arriveBy
                };
            }
        } else if (this.loadMore === 'AFTER') {
            const latestStartTime =
                this.storedItineraries.length > 0
                    ? Math.max(...this.storedItineraries.map((itinerary) => itinerary.startTime))
                    : null;

            if (latestStartTime) {
                const zonedDateTime = Temporal.Instant.fromEpochMilliseconds(latestStartTime).toZonedDateTimeISO(
                    this.timeZone
                );

                searchFrom = {
                    date: zonedDateTime.toPlainDate().toString(),
                    time: zonedDateTime.toPlainTime().toString(),
                    arrive: this.arriveBy
                };
            }
        }

        if (this.env?.JOURNEY_PLANNER?.PROVIDER === 'ETIS') {
            const allMemoryStops = this.openTripPlannerService.getStops();
            if (allMemoryStops.size === 0) {
                await this.openTripPlannerService
                    .getAllStops()
                    .pipe(take(1), takeUntil(this.componentDestroyed$))
                    .toPromise();
            }

            const originStop = this.openTripPlannerFacade.getStopByNameFromMemory(
                search.origin.name,
                search.origin.coordinates,
                search.origin.stopId
            );
            const destinationStop = this.openTripPlannerFacade.getStopByNameFromMemory(
                search.destination.name,
                search.destination.coordinates,
                search.destination.stopId
            );

            payload = {
                loadMore: this.loadMore ?? '',
                fromPlace: originStop
                    ? originStop.id
                    : `LatLng(${search.origin.coordinates.lat},${search.origin.coordinates.lng})`,
                toPlace: destinationStop
                    ? destinationStop.id
                    : `LatLng(${search.destination.coordinates.lat},${search.destination.coordinates.lng})`,
                fromPointType: originStop ? 'STOP_AREA' : 'COORDINATE',
                toPointType: destinationStop ? 'STOP_AREA' : 'COORDINATE',
                filters: search.filters,
                searchDateAndTime: searchFrom ?? search.searchDateAndTime
            };
        } else {
            payload = {
                fromPlace: { ...search.origin.coordinates, name: search.origin.name },
                toPlace: { ...search.destination.coordinates, name: search.destination.name },
                filters: search.filters,
                searchDateAndTime: search.searchDateAndTime,
                arriveBy: search.searchDateAndTime?.arrive
            };
        }
        return payload;
    }

    isSameLocations(location1, location2) {
        if (!location1 || !location2) {
            return false;
        }

        return location1.lat === location2.lat && location1.lng === location2.lng;
    }

    private setCurrentTimeIntervalUpdate() {
        if (this._currentTimeSubscription) {
            this._currentTimeSubscription.unsubscribe();
        }

        this.currentTime$ = new BehaviorSubject(this.getCurrentTimeInMs());

        this.currentTime$.next(this.getCurrentTimeInMs());

        const initialDelaySeconds =
            this.getSecondsToNextUpdate(new Date().getSeconds(), this.env.JOURNEY_PLANNER.UPDATE_INTERVAL_SECONDS) *
            TimeUtil.MILLISECONDS_IN_SECOND;
        // concat depends on the params order, don't change the order; if you think that we should use switchMap instead - please rewrite it
        this._currentTimeSubscription = concat(
            /** Start updating time representation only when we reach the new part of minute
             *  @see - getSecondsToNextUpdate
             *  @reason: to prevent UI blinks, and trigger update in time. */
            timer(initialDelaySeconds),
            interval(this.env.JOURNEY_PLANNER.UPDATE_INTERVAL_SECONDS * TimeUtil.MILLISECONDS_IN_SECOND)
        )
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(() => {
                this.currentTime$.next(this.getCurrentTimeInMs());
            });
    }
}
